import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Sun, Moon, Menu as MenuIcon } from 'lucide-react';

// Import local images instead of remote URLs
import logoLight from '../assets/images/bqlo fon LOGO.webp';
import logoDark from '../assets/images/tumen fon LOGO.webp';

const Navigation = ({ cartCount, onCartClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode');
    if (isDark === null) {
      setDarkMode(true);
      localStorage.setItem('darkMode', 'true');
      document.documentElement.classList.add('dark');
    } else if (isDark === 'true') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'bg' ? 'en' : 'bg';
    i18n.changeLanguage(newLang);
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/^\/(bg|en)/, `/${newLang}`);
    window.history.pushState({}, '', newPath || `/${newLang}`);
  };

  const navLinks = [
    { key: 'home', to: `/${i18n.language}` },
    { key: 'about', to: `/${i18n.language}/about` },
    { key: 'events', to: `/${i18n.language}/events` },
    { key: 'gallery', to: `/${i18n.language}/gallery` },
    { key: 'shop', to: `/${i18n.language}/shop` },
  ];

  const isHomePage = location.pathname === `/${i18n.language}` || location.pathname === `/${i18n.language}/`;

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled || !isHomePage
            ? 'bg-background border-b border-border py-4' 
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between">
            <Link to={`/${i18n.language}`} className="flex items-center space-x-3">
              <img 
                src={darkMode ? logoDark : logoLight} 
                alt="N94" 
                className="w-20 h-20 object-contain"
              />
              <span className={`font-heading text-2xl font-bold transition-colors ${
                scrolled || !isHomePage ? 'text-foreground' : 'text-white'
              }`}>
                Nationals '94
              </span>
            </Link>

            <div className="nav-desktop items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.to}
                  data-testid={`nav-${link.key}`}
                  className={`text-base font-medium transition-colors ${
                    scrolled || !isHomePage
                      ? 'text-foreground hover:text-accent' 
                      : 'text-white hover:text-accent'
                  }`}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              ))}

              <div className="flex items-center gap-6">
                <button
                  onClick={onCartClick}
                  className="relative p-2"
                  data-testid="cart-icon"
                >
                  <ShoppingCart className={`w-5 h-5 transition-colors ${
                    scrolled || !isHomePage
                      ? 'text-foreground hover:text-accent'
                      : 'text-white hover:text-accent'
                  }`} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-accent text-white text-[11px] font-bold flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={toggleDarkMode}
                  className="p-2"
                  data-testid="theme-toggle"
                >
                  {darkMode ? (
                    <Sun className={`w-5 h-5 transition-colors ${
                      scrolled || !isHomePage
                        ? 'text-foreground hover:text-accent'
                        : 'text-white hover:text-accent'
                    }`} />
                  ) : (
                    <Moon className={`w-5 h-5 transition-colors ${
                      scrolled || !isHomePage
                        ? 'text-foreground hover:text-accent'
                        : 'text-white hover:text-accent'
                    }`} />
                  )}
                </button>

                <button
                  onClick={toggleLanguage}
                  data-testid="language-toggle"
                  className={`px-4 py-2 text-sm font-medium border transition-all ${
                    scrolled || !isHomePage
                      ? 'text-foreground border-border hover:bg-secondary'
                      : 'text-white border-white/30 hover:bg-white/10'
                  }`}
                >
                  {i18n.language === 'bg' ? 'EN' : 'BG'}
                </button>

                <Link
                  to={`/${i18n.language}/donate`}
                  data-testid="nav-donate-cta"
                  className={`font-medium text-sm px-6 py-3 transition-all ${
                    scrolled || !isHomePage
                      ? 'bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground'
                      : 'bg-white text-black hover:bg-gray-200'
                  }`}
                >
                  {t('nav.donate')}
                </Link>
              </div>
            </div>

            <div className="nav-mobile">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onCartClick}
                  className="relative p-2"
                  aria-label={`${i18n.language === 'bg' ? 'Количка' : 'Cart'}: ${cartCount}`}
                >
                  <ShoppingCart className={`w-6 h-6 ${
                    scrolled || !isHomePage ? 'text-foreground' : 'text-white'
                  }`} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-accent text-white text-[11px] font-bold flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-toggle"
                  className="p-2"
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6 text-foreground" />
                  ) : (
                    <MenuIcon className={`w-6 h-6 ${
                      scrolled || !isHomePage ? 'text-foreground' : 'text-white'
                    }`} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-0 bg-background z-50 overflow-y-auto"
          >
            <div className="min-h-screen flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center space-x-3">
                  <img 
                    src={darkMode ? logoDark : logoLight} 
                    alt="N94" 
                    className="w-14 h-14 object-contain"
                  />
                  <span className="font-heading text-xl font-bold text-foreground">
                    Nationals '94
                  </span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-secondary transition-colors"
                >
                  <X className="w-6 h-6 text-foreground" />
                </button>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="space-y-6 text-center">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.key}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-3 text-foreground hover:text-accent transition-all font-heading text-2xl font-semibold"
                      >
                        {t(`nav.${link.key}`)}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="p-6 border-t border-border space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={toggleDarkMode}
                    className="p-4 border border-border hover:bg-secondary transition-all flex items-center justify-center gap-2"
                  >
                    {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    <span className="text-sm font-medium">
                      {darkMode ? (i18n.language === 'bg' ? 'Светъл' : 'Light') : (i18n.language === 'bg' ? 'Тъмен' : 'Dark')}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      toggleLanguage();
                      setMobileMenuOpen(false);
                    }}
                    className="p-4 border border-border hover:bg-secondary transition-all font-medium"
                  >
                    {i18n.language === 'bg' ? 'English' : 'Български'}
                  </button>
                </div>

                <Link
                  to={`/${i18n.language}/donate`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block bg-primary text-primary-foreground font-medium text-center px-6 py-4 hover:bg-accent transition-all"
                >
                  {t('nav.donate')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Link
        to={`/${i18n.language}/donate`}
        data-testid="sticky-donate-button"
        className="fixed bottom-8 right-8 z-40 bg-primary text-primary-foreground font-medium px-8 py-4 hover:bg-accent hover:text-accent-foreground transition-all duration-300 shadow-2xl hidden md:block"
      >
        {t('nav.donate')}
      </Link>
    </>
  );
};

export default Navigation;